/***
 * Created on 2024/7/31
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useMemo, useState} from 'react';
import SocialAccount from "../common/social/social_account";
import TextInput from "../common/form/text_input";
import PasswordInput from "../common/form/password_input";
import Captcha from "../common/captcha";
import GdprDialog from "./gdpr_dialog";
import {useTranslation} from "react-i18next";
import {useOnChange, useLanguage} from "common/Hook";
import {getErrorMessage, convertToQNAPLang, getPrivacyPolicyUrl, URLHandler} from 'common/ServiceCommon';


function SignupChinaComponent(props) {
    const { t , i18n} = useTranslation();
    const [isValidQid, setIsValidQid] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [passwordMatch, setPasswordMatch] = useState(false);
    const [disclaimer, setDisclaimer] = useState(false);
    const [showGDPR, setShowGDPR] = useState(false);
    const {values, setValues, onChange} = useOnChange({
        qid: '',
        email: '',
        display_name: '',
        password: '',
        confirm_password: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        captcha: '',
        site: 'CHINA'
    });

    const urlHandler = new URLHandler(location.href);

    const onQidBlur = (e, cb) => {
        cb();
        onChange(e);
        const qid_expr = /^[0-9]{6,16}$/gm;
        const res = qid_expr.test(e.target.value);
        setIsValidQid(res);
    }

    const onEmailBlur = (e) => {
        onChange(e);
        const qid_expr = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/gm;
        const res = qid_expr.test(e.target.value);
        if (e.target.value === "") {
            setIsValidEmail(true);
        }
        else {
            setIsValidEmail(res);
        }
    }

    const isValid = useMemo(()=> {
        return disclaimer && values?.qid && values.password && passwordMatch && isValidQid && isValidEmail;
    }, [values.qid, values.password, values.confirm_password, disclaimer, passwordMatch, isValidQid, isValidEmail]);

    return (
        <section className="common-page-wrapper common-page-wrapper--sign-up" data-location="cn">
            <div className="common-page-header">{t('@Create Account@')}</div>
            <div className="common-page-body">
                <div className="sign-up--regular">
                    <div className="sign-up--regular__group">
                        <div className="common-form-group">
                            <div className="comp-input !mb-24">
                                <TextInput label={t('@Display Name@')} placeholder={t('@Display Name@')}
                                           name='display_name'
                                           maxLength={20}
                                           value={values.display_name} onChange={onChange}/>
                            </div>

                            <div className="comp-input !mb-24">
                                <div className="input-field--hybrid">
                                    <div className="comp-select lock">
                                        <div className="q-select cursor-pointer">
                                            <div className="q-select__label">中国大陆 (+86)</div>
                                            <div className="q-select__arrow"></div>
                                        </div>
                                    </div>
                                    <TextInput inputMode="numeric" minLength={6} maxLength={11} pattern="[0-9]*" label={t('@Contact Phone@')} placeholder={t('@Contact Phone@')}
                                               value={values.qid} onChange={onQidBlur} name="qid" required={true} noWrap={true}/>
                                </div>
                            </div>

                            <div className="comp-input !mb-10">
                                 <TextInput label={`${t('@Email@')} (${t('@Optional@')})`} placeholder={`${t('@Email@')} (${t('@Optional@')})`}
                                           inputMode={"email"} name='email' value={values.email} onChange={onEmailBlur}
                                           pattern="^([a-zA-Z0-9_\-\.]+)@[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,63}$"/>
                            </div>
                            <p className="!mb-24">{t('@Setup an email allows you to sign in with your phone number or email You can also use it as a backup method to receive 2-Step Verification code@')}</p>

                            <PasswordInput
                                label={t('@Password@')}
                                placeholder={t('@Password@')}
                                name='password'
                                value={values.password}
                                onChange={onChange}
                                required={true}
                                showStrength={true}
                                ignoreRules={false}
                                showTip={true}/>

                            <PasswordInput
                                label={t('@Confirm Password@')}
                                placeholder={t('@Confirm Password@')}
                                name='confirm_password'
                                value={values.confirm_password}
                                onChange={onChange}
                                status={setPasswordMatch}
                                matchInput={values.password}
                                required={true}/>

                            <Captcha value={values.captcha} inputChangeHandler={onChange}
                                     resetValue={() => setValues(prev => ({...prev, captcha: ''}))}/>
                        </div>
                    </div>

                    <div className="common-form-group">
                        <SocialAccount region="CHINA" isAgreed={disclaimer} action="check_linked" setValues={setValues}/>
                        <div className="comp-checkbox">
                            <label className="q-checkbox">
                                <input type="checkbox" name="checkbox-terms" checked={disclaimer}
                                       onChange={() => setDisclaimer(prev => !prev)}/>
                                <span dangerouslySetInnerHTML={
                                    {
                                        __html: t('@I agree to myQNAPcloud Terms of Service and Privacy Policy.@')
                                            .replace(`<a ng-click='showTermsOfUse()'>`, `<a href="${window.acc_mf.common.APP_QNAP_URL}/${convertToQNAPLang(i18n.language)}/legal/qnap-cloud-service-terms-and-conditions" target="_blank">`)
                                            .replace(`<a ng-click='showPrivacyPolicy()'>`, `<a href="${getPrivacyPolicyUrl()}" target="_blank">`)
                                    }
                                }></span>
                            </label>
                        </div>
                    </div>
                    <button className="q-btn q-btn--primary--focus btn-medium btn-rounded-normal" disabled={!isValid}
                            onClick={() => setShowGDPR(true)}>{t('@Sign Up@')}</button>
                    <div className="sign-up--options">
                        <p className="!mb-10"><a href="/signin">{t('@Already have an account?@')}</a></p>
                        {!urlHandler.isHideActivationMail() &&
                            <p><a href="#send_activation_email" onClick={e => {
                                location.href = "/signup#send_activation_email";
                                location.reload()
                            }}>{t('@Have not received activation email?@')}</a></p>
                        }
                    </div>
                </div>
            </div>
            {showGDPR && <GdprDialog showModal={showGDPR} closeModal={() => setShowGDPR(false)} data={values}/>}
        </section>
    );
}

export default SignupChinaComponent;